import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '@y42/environment';
import { ORG_API_URL_PARAM } from '@y42/model';
import { map, Observable } from 'rxjs';
import { IdpModel, IdpProtocol, IdpSettingsModel, IdpType } from '../../../models/sso-idp.model';
import { GenericSamlCreateModel, GenericSamlModel } from './saml/sso-generic-saml.model';
import { IdpProtocolToDiscoverSettingsModel } from './sso-idp.util';
import { OauthSetupModel, OauthModel, OauthCreateModel } from './oauth/sso-oauth.model';
import { SamlSetupModel } from './saml/sso-saml.model';

@Injectable()
export class SsoIdpHttpService {
  constructor(private readonly httpClient: HttpClient) {}

  private readonly url = `${environment.url.company_api}/orgs/${ORG_API_URL_PARAM}/idps`;

  getAll(): Observable<IdpModel[]> {
    return this.httpClient.get<{ data: IdpModel[] }>(this.url).pipe(map(({ data }) => data));
  }

  delete(publicId: string): Observable<void> {
    return this.httpClient.delete<unknown>(`${this.url}/${publicId}`).pipe(map(() => undefined));
  }

  getOauthSetup(): Observable<OauthSetupModel> {
    return this.httpClient.get<OauthSetupModel>(`${this.url}/oauth2`);
  }

  createOauth(oauthCreate: OauthCreateModel): Observable<OauthModel> {
    return this.httpClient.post<OauthModel>(`${this.url}/oauth2`, oauthCreate);
  }

  updateOauth(oauthUpdate: OauthModel): Observable<OauthModel> {
    const { public_id, ...payload } = oauthUpdate;
    return this.httpClient.put<OauthModel>(`${this.url}/oauth2/${public_id}`, payload);
  }

  getSamlSetup(): Observable<SamlSetupModel> {
    return this.httpClient.get<SamlSetupModel>(`${this.url}/saml2`);
  }

  createGenericSaml<TSettings extends IdpSettingsModel = IdpSettingsModel, TIdpType extends IdpType = IdpType>(
    samlCreate: GenericSamlCreateModel<TSettings, TIdpType>,
  ): Observable<GenericSamlModel<TSettings, TIdpType>> {
    return this.httpClient.post<GenericSamlModel<TSettings, TIdpType>>(`${this.url}/saml2`, samlCreate);
  }

  updateGenericSaml<TSettings extends IdpSettingsModel = IdpSettingsModel, TIdpType extends IdpType = IdpType>(
    samlUpdate: GenericSamlModel<TSettings, TIdpType>,
  ): Observable<GenericSamlModel<TSettings, TIdpType>> {
    const { public_id, ...payload } = samlUpdate;
    return this.httpClient.put<GenericSamlModel<TSettings, TIdpType>>(`${this.url}/saml2/${public_id}`, payload);
  }

  discoverSettings<T extends IdpProtocol>(
    metadataUrl: string,
    protocol: T,
  ): Observable<IdpProtocolToDiscoverSettingsModel[T]> {
    return this.httpClient
      .get<{ data: IdpProtocolToDiscoverSettingsModel[T] }>(`${environment.url.company_api}/${protocol}/discover`, {
        params: { metadata_url: metadataUrl },
      })
      .pipe(map(({ data }) => data));
  }
}
